import { Hand, Mic, MicOff, PenLine, Search, ShieldCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { SignalBars } from '@/components/signal-bars';
import { UserAvatar } from '@/components/user-avatar';
import { cn } from '@/lib/utils';

import type { ParticipantRole } from '../realtime/frames';
import { signalLevel } from './roomState';

/**
 * The «Зрители» tab of the webinar side panel: the raised-hands section on top and the
 * presence roster below it. For hosts (moderator/admin) every viewer row carries the speak
 * action - «Разрешить говорить» grants (allow-speak testid), «Запретить говорить» revokes
 * (speak-grant testid) - both POST/DELETE the REST speak endpoints; the roster converges on
 * the speak_grant frame echo. The rows carry the same pair for board grants (ADR 0013:
 * allow-board / board-grant testids, REST board endpoints, board_grant frames). Viewers see
 * the same roster read-only.
 */
export interface ViewersPanelProps {
  /** Connected participants (identity -> presence), the state frame snapshot kept current. */
  readonly participants: ReadonlyMap<string, ViewerEntry>;
  readonly ownIdentity: string | null;
  /** True for moderator/admin: the speak and board grant/revoke buttons render. */
  readonly isHost: boolean;
  /** Identities with a live speak grant (from speak_grant frames). */
  readonly grantedIds: ReadonlySet<string>;
  /** Identities with a grant/revoke request in flight (button disabled). */
  readonly busyIds: ReadonlySet<string>;
  /** Identities holding a board-editor grant (board_grant frames, ADR 0013). */
  readonly boardGrantedIds: ReadonlySet<string>;
  /** Identities with a board grant/revoke request in flight (button disabled). */
  readonly boardBusyIds: ReadonlySet<string>;
  readonly onGrantSpeak: (identity: string) => void;
  readonly onRevokeSpeak: (identity: string) => void;
  readonly onGrantBoard: (identity: string) => void;
  readonly onRevokeBoard: (identity: string) => void;
  /**
   * Every member's LiveKit connection quality by identity (the unified room header): each
   * roster row carries its bars. Undefined keeps the rows bar-less (tests, legacy callers).
   */
  readonly quality?: ReadonlyMap<string, string> | undefined;
}

/** One roster entry the panel renders. */
export interface ViewerEntry {
  readonly displayName: string;
  readonly role: ParticipantRole;
  readonly handRaised: boolean;
}

function matches(query: string, entry: ViewerEntry, identity: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return true;
  }
  return (
    entry.displayName.toLowerCase().includes(needle) || identity.toLowerCase().includes(needle)
  );
}

export function ViewersPanel(props: ViewersPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const { raised, rest } = useMemo(() => {
    const raisedEntries: Array<{ identity: string; entry: ViewerEntry }> = [];
    const restEntries: Array<{ identity: string; entry: ViewerEntry }> = [];
    for (const [identity, entry] of props.participants) {
      if (!matches(query, entry, identity)) {
        continue;
      }
      (entry.handRaised ? raisedEntries : restEntries).push({ identity, entry });
    }
    raisedEntries.sort((a, b) => a.identity.localeCompare(b.identity));
    restEntries.sort((a, b) => a.identity.localeCompare(b.identity));
    return { raised: raisedEntries, rest: restEntries };
  }, [props.participants, query]);

  return (
    <div data-testid="viewers-panel" className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 px-3 pt-3">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-zinc-600"
          />
          <input
            data-testid="viewers-search"
            aria-label={t('webinar.viewers_search_aria')}
            placeholder={t('webinar.viewers_search_placeholder')}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            className="h-8 w-full rounded-lg border border-white/10 bg-white/[0.05] pr-3 pl-8 text-[12px] text-white outline-none placeholder:text-zinc-600 focus:border-white/20"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-3 scroll-thin-dark">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.14em] text-amber-400 uppercase">
          <Hand className="size-3" />
          {t('webinar.hands_raised')}
          <span className="ml-auto tabular-nums">{raised.length}</span>
        </div>
        {raised.length === 0 ? (
          <p className="mt-1.5 rounded-lg border border-dashed border-white/[0.06] px-2.5 py-2 text-[11px] text-zinc-600">
            {t('webinar.no_hands')}
          </p>
        ) : (
          <ul className="mt-1.5 space-y-1">
            {raised.map(({ identity, entry }) => (
              <li key={`hand-${identity}`}>
                <ViewerRow
                  identity={identity}
                  entry={entry}
                  own={identity === props.ownIdentity}
                  isHost={props.isHost}
                  granted={props.grantedIds.has(identity)}
                  busy={props.busyIds.has(identity)}
                  boardGranted={props.boardGrantedIds.has(identity)}
                  boardBusy={props.boardBusyIds.has(identity)}
                  onGrantSpeak={props.onGrantSpeak}
                  onRevokeSpeak={props.onRevokeSpeak}
                  onGrantBoard={props.onGrantBoard}
                  onRevokeBoard={props.onRevokeBoard}
                  highlighted
                />
              </li>
            ))}
          </ul>
        )}

        <div className="mt-3 flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.14em] text-zinc-500 uppercase">
          {t('webinar.viewers_tab')}
          <span className="ml-auto tabular-nums">{rest.length}</span>
        </div>
        {rest.length === 0 ? (
          <p className="mt-1.5 text-center text-[11px] text-zinc-600">{t('webinar.no_viewers')}</p>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {rest.map(({ identity, entry }) => (
              <li key={identity}>
                <ViewerRow
                  identity={identity}
                  entry={entry}
                  own={identity === props.ownIdentity}
                  isHost={props.isHost}
                  granted={props.grantedIds.has(identity)}
                  busy={props.busyIds.has(identity)}
                  boardGranted={props.boardGrantedIds.has(identity)}
                  boardBusy={props.boardBusyIds.has(identity)}
                  onGrantSpeak={props.onGrantSpeak}
                  onRevokeSpeak={props.onRevokeSpeak}
                  onGrantBoard={props.onGrantBoard}
                  onRevokeBoard={props.onRevokeBoard}
                  highlighted={false}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface ViewerRowProps {
  readonly identity: string;
  readonly entry: ViewerEntry;
  readonly own: boolean;
  readonly isHost: boolean;
  readonly granted: boolean;
  readonly busy: boolean;
  readonly boardGranted: boolean;
  readonly boardBusy: boolean;
  readonly highlighted: boolean;
  readonly onGrantSpeak: (identity: string) => void;
  readonly onRevokeSpeak: (identity: string) => void;
  readonly onGrantBoard: (identity: string) => void;
  readonly onRevokeBoard: (identity: string) => void;
  /**
   * Every member's LiveKit connection quality by identity (the unified room header): each
   * roster row carries its bars. Undefined keeps the rows bar-less (tests, legacy callers).
   */
  readonly quality?: ReadonlyMap<string, string> | undefined;
}

/** One roster row: avatar, name, role badge and (for hosts) the speak and board actions. */
function ViewerRow(props: ViewerRowProps): JSX.Element {
  const { t } = useTranslation();
  const { entry, identity } = props;
  const name = entry.displayName.length > 0 ? entry.displayName : identity;
  // Staff (admins/moderators) are roster, not grant targets: they publish media and draw by
  // role - their join tokens carry the grants. Only plain participants are grantable: they
  // join webinar rooms subscribe-only until a speak grant mints them a token.
  const grantable = props.isHost && !props.own && entry.role === 'participant';
  // An editor is a moderator/admin by role or an identity holding a board grant (ADR 0013).
  const boardEditor = entry.role === 'admin' || entry.role === 'moderator' || props.boardGranted;

  return (
    <div
      data-testid={`viewer-row-${identity}`}
      className={cn(
        'group flex items-center gap-2.5 rounded-lg px-2 py-1.5',
        props.highlighted
          ? 'border border-amber-400/15 bg-amber-400/[0.05]'
          : 'hover:bg-white/[0.04]',
      )}
    >
      <UserAvatar name={name} size="sm" className="size-6 text-[9px]" />
      {props.quality !== undefined ? (
        <span
          data-testid={`viewer-quality-${identity}`}
          title={t('participants.quality_title')}
          className="shrink-0"
        >
          <SignalBars level={signalLevel(props.quality.get(identity) ?? 'unknown')} />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 truncate text-[12.5px] text-zinc-300">
            {props.own ? t('webinar.viewers_you') : name}
          </span>
          {entry.role === 'admin' ? (
            <span
              data-testid={`role-badge-${identity}`}
              className="shrink-0 rounded bg-amber-400/15 px-1 py-px text-[9.5px] font-bold tracking-wide text-amber-400 uppercase"
            >
              <ShieldCheck className="inline size-2.5" /> {t('participants.badge_host')}
            </span>
          ) : entry.role === 'moderator' ? (
            <span
              data-testid={`role-badge-${identity}`}
              className="shrink-0 rounded bg-sky-500/15 px-1 py-px text-[9.5px] font-bold tracking-wide text-sky-300 uppercase"
            >
              {t('participants.badge_moderator')}
            </span>
          ) : null}
        </div>
      </div>
      <span
        className="shrink-0"
        data-testid={`viewer-mic-${identity}`}
        aria-label={t(
          grantedLive(props.granted, entry) ? 'participants.mic_on' : 'participants.mic_muted',
        )}
      >
        {grantedLive(props.granted, entry) ? (
          <Mic className="size-3.5 text-emerald-400" />
        ) : (
          <MicOff className="size-3.5 text-zinc-600" />
        )}
      </span>
      <span
        className="shrink-0"
        data-testid={`viewer-board-${identity}`}
        aria-label={t(boardEditor ? 'board.editor_aria' : 'board.viewer_aria')}
      >
        {boardEditor ? (
          <PenLine className="size-3.5 text-sky-300" />
        ) : (
          <PenLine className="size-3.5 text-zinc-700" />
        )}
      </span>
      {grantable ? (
        props.granted ? (
          <button
            type="button"
            data-testid={`speak-grant-${identity}`}
            disabled={props.busy}
            onClick={() => {
              props.onRevokeSpeak(identity);
            }}
            className="h-6 shrink-0 rounded-md border border-red-400/30 bg-red-400/10 px-2 text-[10.5px] font-medium text-red-300 transition-colors hover:bg-red-400/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('webinar.revoke_speak')}
          </button>
        ) : (
          <button
            type="button"
            data-testid={`allow-speak-${identity}`}
            disabled={props.busy}
            onClick={() => {
              props.onGrantSpeak(identity);
            }}
            className="h-6 shrink-0 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-2 text-[10.5px] font-medium text-emerald-400 transition-colors hover:bg-emerald-400/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('webinar.allow_speak')}
          </button>
        )
      ) : null}
      {grantable ? (
        props.boardGranted ? (
          <button
            type="button"
            data-testid={`board-grant-${identity}`}
            disabled={props.boardBusy}
            onClick={() => {
              props.onRevokeBoard(identity);
            }}
            className="h-6 shrink-0 rounded-md border border-red-400/30 bg-red-400/10 px-2 text-[10.5px] font-medium text-red-300 transition-colors hover:bg-red-400/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('board.revoke_draw')}
          </button>
        ) : (
          <button
            type="button"
            data-testid={`allow-board-${identity}`}
            disabled={props.boardBusy}
            onClick={() => {
              props.onGrantBoard(identity);
            }}
            className="h-6 shrink-0 rounded-md border border-sky-400/30 bg-sky-400/10 px-2 text-[10.5px] font-medium text-sky-300 transition-colors hover:bg-sky-400/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('board.allow_draw')}
          </button>
        )
      ) : null}
    </div>
  );
}

/** A row shows a live microphone when the entry publishes: staff by role, viewers by grant
 * (a legacy speak_grant frame for a staff identity renders the same harmless live mark). */
function grantedLive(granted: boolean, entry: ViewerEntry): boolean {
  return entry.role === 'admin' || entry.role === 'moderator' || granted;
}
